import { Modal, Form } from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";
import { ServerRequestsSubscriptionGroupsResponse } from "./shared/api/axios-client";
import { Button } from "./Controls/Button";
import { HeaderText } from "./Controls/HeaderText";

type Props = {
  show: boolean;
  onClose: () => void;
  group?: ServerRequestsSubscriptionGroupsResponse;
  existingGroups: ServerRequestsSubscriptionGroupsResponse[];
  onSubmit: (
    name: string,
    groupId?: number,
    options?: { addParkersAfterSave?: boolean }
  ) =>
    | Promise<ServerRequestsSubscriptionGroupsResponse | void>
    | ServerRequestsSubscriptionGroupsResponse
    | void;
};

export function GroupModal({
  show,
  onClose,
  group,
  existingGroups,
  onSubmit,
}: Props) {
  const isEdit = !!group;

  const [name, setName] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [addParkersAfterSave, setAddParkersAfterSave] = useState(false);

  useEffect(() => {
    if (group) {
      setName(group.name!);
    } else {
      setName("");
    }
    setAddParkersAfterSave(false);
    setTouched(false);
  }, [group, show]);

  const error = useMemo(() => {
    if (!touched) return "";

    if (!name.trim()) return "Group name is required.";
    if (name.length < 3) return "Group name must be at least 3 characters.";
    if (name.length > 64) return "Group name must be less than 64 characters.";

    const duplicate = existingGroups.some(
      g =>
        g.name!.toLowerCase() === name.toLowerCase() &&
        g.id !== group?.id
    );

    if (duplicate) return "A group with this name already exists.";

    return "";
  }, [name, touched, existingGroups, group]);

  const isValid = !error && name.trim().length > 0;

  const handleSubmit = async () => {
    if (!isValid) return;

    setSubmitting(true);
    await onSubmit(name.trim(), group?.id, {
      addParkersAfterSave: !isEdit && addParkersAfterSave,
    });
    setSubmitting(false);
    onClose();
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <HeaderText>
          {isEdit ? "Edit Group" : "Add Group"}
        </HeaderText>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Form.Group>
            <Form.Label>Group name</Form.Label>
            <Form.Control
              value={name}
              autoFocus
              placeholder="Enter group name"
              isInvalid={!!error}
              onChange={(e) => {
                setName(e.target.value);
                setTouched(true);
              }}
            />
            <Form.Control.Feedback type="invalid">
              {error}
            </Form.Control.Feedback>
          </Form.Group>

          {!isEdit && (
            <Form.Group className="mt-3">
              <Form.Check
                type="switch"
                id="add-parkers-after-save"
                label="Add parkers after save"
                checked={addParkersAfterSave}
                onChange={(e) => setAddParkersAfterSave(e.target.checked)}
              />
              <div className="form-text text-muted">
                You'll be redirected to the Manage Parkers after saving.
              </div>
            </Form.Group>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </Button>

        <Button
          className="btn-main"
          disabled={!isValid || submitting}
          onClick={handleSubmit}
        >
          {isEdit ? "Save Changes" : "Add Group"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}